import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ConnectError, Code } from '@connectrpc/connect';
import type { ActionDescriptor } from './actions';

// Write seam for view-primitives (HUB-407) — the mutation counterpart to the
// read-side BladeDataSource. Primitives don't know how a submit reaches the
// backend: they call useSubmitAction, which resolves against the BladeActionSource
// the shell provides. The routed shell (/w) provides a live @hub/api source that
// calls WorkQueueService RPCs; the backendless preview + tests provide a mock.
//
// This is the path through which user actions ENTER the system (and, via the
// server's activity stream, the event ledger — HUB-320). PHI discipline mirrors
// the read seam: never log submitted values, keep only entity ids (never payloads)
// in query keys, and surface permission failures as a "denied" state rather than a
// generic error. Server-side authz/lifecycle validation is authoritative — the
// client offers actions and renders the server's acceptance or rejection.

/** Field-keyed validation messages the server rejected a submit with. Keys match
 *  form field names (and ViewDefinition field keys, HUB-408) so a form can render
 *  each message inline against its control. */
export type ActionFieldErrors = Record<string, string>;

/** Outcome of a successful submit. `invalidateEntityIds` names the entities whose
 *  cached read queries must refresh so open blades don't go stale (the liveness
 *  concern from the review); defaults to the descriptor's own entityId. No PHI. */
export interface ActionSubmitResult {
  invalidateEntityIds?: string[];
}

/** Thrown by a source when the server rejects a submit as *invalid* — a bad value
 *  or an illegal lifecycle transition (ConnectRPC InvalidArgument / FailedPrecondition).
 *  Carries a form-level message and/or per-field messages so the form renders them
 *  inline, distinct from an unexpected blade-level failure. */
export class ActionValidationError extends Error {
  readonly fieldErrors: ActionFieldErrors;
  constructor(message: string, fieldErrors: ActionFieldErrors = {}) {
    super(message);
    this.name = 'ActionValidationError';
    this.fieldErrors = fieldErrors;
  }
}

export interface BladeActionSource {
  /** Submit a mutation described by `descriptor` with the form's `values`. Resolves
   *  with the entities to invalidate on success; rejects with ActionValidationError
   *  for invalid submits, a ConnectError (permission/auth → denied) otherwise. */
  submitAction(
    descriptor: ActionDescriptor,
    values: Record<string, string>,
  ): Promise<ActionSubmitResult>;
}

const BladeActionContext = createContext<BladeActionSource | null>(null);

export function BladeActionProvider({
  source,
  children,
}: {
  source: BladeActionSource;
  children: ReactNode;
}) {
  return <BladeActionContext.Provider value={source}>{children}</BladeActionContext.Provider>;
}

function useActionSource(): BladeActionSource {
  const s = useContext(BladeActionContext);
  if (!s) throw new Error('useSubmitAction requires a BladeActionProvider');
  return s;
}

export type ActionStatus = 'idle' | 'pending' | 'success' | 'error' | 'denied';

export interface UseSubmitAction {
  /** Fire-and-forget submit (errors land in `status`/`fieldErrors`/`formError`). */
  submit: (values: Record<string, string>) => void;
  /** Promise variant for callers that need to await the outcome. */
  submitAsync: (values: Record<string, string>) => Promise<ActionSubmitResult>;
  status: ActionStatus;
  /** per-field validation messages from the last rejected submit */
  fieldErrors: ActionFieldErrors;
  /** form-level message: a validation message with no field, or a generic failure */
  formError?: string;
  /** clear the last outcome (e.g. when the user edits after an error) */
  reset: () => void;
}

/** Invalidate the read queries an action touched: any list blade (the change may
 *  reorder/relabel the worklist) plus the entity blades for the affected ids. Keys
 *  mirror BladeDataSource.keyFor: ['blade', kind, key, entityId, params]. */
function invalidateAffected(queryClient: QueryClient, entityIds: string[]): void {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const k = query.queryKey as unknown[];
      if (k[0] !== 'blade') return false;
      if (k[1] === 'list') return true;
      if (k[1] === 'entity' && typeof k[3] === 'string' && entityIds.includes(k[3])) return true;
      return false;
    },
  });
}

function classifyError(err: unknown): 'validation' | 'denied' | 'error' {
  if (err instanceof ActionValidationError) return 'validation';
  if (
    err instanceof ConnectError &&
    (err.code === Code.PermissionDenied || err.code === Code.Unauthenticated)
  ) {
    return 'denied';
  }
  return 'error';
}

/** Bind a submit handler for one action descriptor to the provided BladeActionSource.
 *  Manages pending/success/error/denied state and, on success, invalidates the
 *  affected read queries so open detail/list blades refresh. */
export function useSubmitAction(descriptor: ActionDescriptor): UseSubmitAction {
  const source = useActionSource();
  const queryClient = useQueryClient();
  const [fieldErrors, setFieldErrors] = useState<ActionFieldErrors>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [errorKind, setErrorKind] = useState<'validation' | 'denied' | 'error' | null>(null);

  const mutation = useMutation({
    mutationFn: (values: Record<string, string>) => source.submitAction(descriptor, values),
    onMutate: () => {
      setFieldErrors({});
      setFormError(undefined);
      setErrorKind(null);
    },
    onSuccess: (result) => {
      const ids = result.invalidateEntityIds ?? (descriptor.entityId ? [descriptor.entityId] : []);
      if (ids.length) invalidateAffected(queryClient, ids);
    },
    onError: (err) => {
      const kind = classifyError(err);
      setErrorKind(kind);
      if (err instanceof ActionValidationError) {
        setFieldErrors(err.fieldErrors);
        setFormError(Object.keys(err.fieldErrors).length ? undefined : err.message);
      } else if (kind === 'error') {
        setFormError('Something went wrong. Please try again.');
      }
    },
  });

  const reset = useCallback(() => {
    setFieldErrors({});
    setFormError(undefined);
    setErrorKind(null);
    mutation.reset();
  }, [mutation]);

  let status: ActionStatus = 'idle';
  if (mutation.isPending) status = 'pending';
  else if (mutation.isSuccess) status = 'success';
  else if (mutation.isError) status = errorKind === 'denied' ? 'denied' : 'error';

  return {
    submit: (values) => mutation.mutate(values),
    submitAsync: (values) => mutation.mutateAsync(values),
    status,
    fieldErrors,
    formError,
    reset,
  };
}
